import { sortBy, orderBy } from 'lodash';
import { createSearchParams } from 'react-router-dom';
import { STATISTICS, ENTITY, META } from './constants';

const isIos = () => /(iPad|iPhone|iPod)/g.test(navigator.userAgent);

const pushToHistoryAndScrollIntoView = (anchor, element) => {
    // TODO: Use the React idiomatic version of replacing history state
    if (window.location?.href && !window.location.href.includes(`#${anchor}`)) {
        window.history.pushState(null, '', `#${anchor}`);
    }
    element?.scrollIntoView({ behavior: 'smooth' });
};

const scrollElementIntoView = (e, anchor) => {
    try {
        if (!isIos()) {
            return;
        }

        if (!anchor) {
            return;
        }

        const element = document.getElementById(anchor);
        if (!element) {
            console.error('There is no target element');
            return;
        }

        e.preventDefault();
        pushToHistoryAndScrollIntoView(anchor, element);
    } catch (error) {
        console.error(error);
    }
};

const scrollElementIntoViewOnMobile = (anchor) => {
    try {
        const mediaQueryM = window.matchMedia('(min-width: 720px)');
        if (mediaQueryM.matches) {
            return;
        }

        if (!anchor) {
            return;
        }

        const element = document.getElementById(anchor);
        if (!element) {
            console.error('There is no target element');
            return;
        }

        pushToHistoryAndScrollIntoView(anchor, element);
    } catch (error) {
        console.error(error);
    }
};

const getDataFromResponse = (response) => response.data.data;
const setErrorStatus = (setError, error) => setError(error?.response?.status ?? true);

const UrlBuilder = {
    [ENTITY.alerts]: ([category]) => `/${category}`,
    [ENTITY.categories]: () => '/categories',
    [ENTITY.categoryDetails]: ([category]) => `/categories/${category}`,
    [ENTITY.entriesByCategoryId]: ([category]) => `/categories/${category}/entries`,
    [ENTITY.entry]: ([category, entry]) => `/categories/${category}/entries/${entry}`,
    [ENTITY.relatedContent]: ([category, entry]) => `/categories/${category}/items/${entry}/related`
};

const StatisticsEnricher = {
    home: {
        enricher: () => 'group=content-eq',
        type: 'location-view'
    },
    search: {
        enricher: ([searchPhrase, searchConfidence]) =>
            `searchtext=${searchPhrase};searchconf=${searchConfidence};group=content-eq`,
        type: 'location-search'
    },
    predictiveSearch: {
        enricher: ([searchPhrase, searchConfidence, entryName]) =>
            `searchtext=${searchPhrase};searchconf=${searchConfidence};name=${entryName};group=content-eq`,
        type: 'location-search-ahead'
    },
    category: {
        enricher: ([categoryId]) => `category=${categoryId}`,
        type: 'location-view'
    },
    entry: {
        enricher: ([entryUid]) => `entry-uid=${entryUid}`,
        type: 'entry-view'
    },
    feedback: {
        enricher: ([entryUid, vote]) => `entry-uid=${entryUid};vote=${vote}`,
        type: 'entry-vote'
    },
    decision: {
        enricher: ([stepUid]) => `entry-uid=${stepUid};vote=1`,
        type: 'make-decision'
    },
    externalContent: {
        enricher: ([url]) => `linkData=${url}`,
        type: 'link-click'
    }
};

const buildRequest = (entity, urlBuilderArgs, params) => {
    const urlBuilder = UrlBuilder[entity];

    return {
        url: urlBuilder(urlBuilderArgs),
        params: params && createSearchParams(params)
    };
};

const buildStatisticsUrl = ({ type, enricher }, ...enricherArgs) => {
    const getStatisticsType = () => `statistic=${type}`;

    return (
        STATISTICS.url +
        '?client=equiniti;usergroup=content-eq;' +
        getStatisticsType() +
        ';' +
        enricher(enricherArgs)
    );
};

const getStatisticsUrlForHome = () =>
    buildStatisticsUrl(StatisticsEnricher.home);

const getStatisticsUrlForSearch = (searchPhrase, searchConfidence) =>
    buildStatisticsUrl(StatisticsEnricher.search, searchPhrase, searchConfidence);

const getStatisticsUrlForPredictiveSearch = (searchPhrase, searchConfidence, entryName) =>
    buildStatisticsUrl(StatisticsEnricher.predictiveSearch, searchPhrase, searchConfidence, entryName);

const getStatisticsUrlForCategory = (categoryId) =>
    buildStatisticsUrl(StatisticsEnricher.category, categoryId);

const getStatisticsUrlForEntry = (entryUid) =>
    buildStatisticsUrl(StatisticsEnricher.entry, entryUid);

const getStatisticsUrlForFeedback = (entryUid, vote) =>
    buildStatisticsUrl(StatisticsEnricher.feedback, entryUid, vote);

const getStatisticsUrlForDecisionTreeDecision = (stepUid) =>
    buildStatisticsUrl(StatisticsEnricher.decision, stepUid);

const getStatisticsUrlForExternalContent = (url) =>
    buildStatisticsUrl(StatisticsEnricher.externalContent, url);

const getStatisticsImg = (statisticsUrl) =>
    <img hidden={true} alt="statistics" src={statisticsUrl} height="0" width="0" />;

const sortByOrderAsc = items => sortBy(items, ['order']);
const sortByConfidenceDesc = items => orderBy(items, ['confidence'], ['desc']);

const getCategoryPageTitle = category => category + ' - ' + META.homePageTitle;
const getCategoryPageDescription = category => `Find answers to questions about '${category}'`;
const getEntryPageTitle = (entry, category) => entry + ' - ' + category + ' - ' + META.homePageTitle;
const getSearchPageTitle = phrase => `Search results for: '${phrase}' - ${META.homePageTitle}`;

export {
    scrollElementIntoView,
    scrollElementIntoViewOnMobile,
    getDataFromResponse,
    setErrorStatus,
    buildRequest,
    getStatisticsUrlForHome,
    getStatisticsUrlForSearch,
    getStatisticsUrlForPredictiveSearch,
    getStatisticsUrlForCategory,
    getStatisticsUrlForEntry,
    getStatisticsUrlForFeedback,
    getStatisticsUrlForDecisionTreeDecision,
    getStatisticsUrlForExternalContent,
    getStatisticsImg,
    sortByOrderAsc,
    sortByConfidenceDesc,
    getCategoryPageTitle,
    getCategoryPageDescription,
    getEntryPageTitle,
    getSearchPageTitle
};
